import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { Field, reduxForm, reset } from 'redux-form';
import moment from 'moment';

import { moveForm } from 'actions';
import usePrevious from 'hooks/usePrevious';

import AutoCompleteInput from 'components/Common/Inputs/AutoCompleteInput';

const MoveForm = ({ dispatch, title, introText, sourceAccountId, forms, isMovingForm, moveFormComplete, onSuccess }) => {
  const [formId, setFormId] = useState(null);
  const [sourceCustomer, setSourceCustomer] = useState(null);
  const [sourceAccount, setSourceAccount] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [account, setAccount] = useState(null);

  const prevIsMovingForm = usePrevious(isMovingForm);

  const clearForm = () => {
    setFormId(null);
    setSourceCustomer(null);
    setSourceAccount(null);
    setCustomer(null);
    setAccount(null);
  };

  useEffect(() => {
    if (prevIsMovingForm && !isMovingForm && moveFormComplete) {
      clearForm();
      dispatch(reset('move-form'));
    }
  }, [isMovingForm, moveFormComplete]);

  const formDescription = (form) => {
    if (sourceAccountId) {
      return `${ form.formTypeDescription } - ${ moment(form.dateUploaded).format('MMM DD, YYYY') } - ${ form.entityName ?? 'N/A' }`;
    }
    return `${ form.formTypeDescription } - ${ moment(form.dateUploaded).format('MMM DD, YYYY') } - ${ form.accountNumber ?? 'N/A' }`;
  };

  const onFormSelected = (value) => {
    if (value && forms) {
      setFormId(parseInt(value, 10));
      const formSelected = forms.filter(f => f.id === parseInt(value, 10));
      if (formSelected && formSelected.length > 0) {
        setSourceCustomer({ id: formSelected[0].customerDbId, customerId: formSelected[0].customerId, name: formSelected[0].customerName });
        if (formSelected[0].accountId) {
          setSourceAccount({ id: formSelected[0].accountId, accountNumber: formSelected[0].accountNumber, name: formSelected[0].accountName });
        }
        return;
      }
    }
    setFormId(null);
    setSourceCustomer(null);
    setSourceAccount(null);
    setCustomer(null);
    setAccount(null);
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    if (formId && sourceCustomer && customer && account) {
      dispatch(moveForm(formId, sourceCustomer.id, sourceAccount?.id ?? sourceAccountId, customer.id, account.id, onSuccess));
    }
  };

  return (<div className="move-clone-form">
    { title && <h2><strong>{ title }</strong></h2> }
    { introText && <p>{ introText }</p> }
    <form onSubmit={ onFormSubmit }>
      <div className="form-group columns">
        <div className="col-1">
          <label htmlFor="move-form-id" className="panel-body-label">Form</label>
        </div>
        <div className="col-6">
          { forms && forms.length > 0
            && <Field
              id="move-form-id"
              name="formId"
              component="select"
              className="form-select"
              required
              value={ formId ? formId.toString() : null }
              onChange={ e => onFormSelected(e.target.value) }
            >
              <option value="">Select form...</option>
              { forms && forms.map(f => (<option key={ f.id } value={ f.id }>{ formDescription(f) }</option>)) }
            </Field> }
          { (!forms || forms.length === 0) && <p><i>No forms suitable to move</i></p> }
        </div>
      </div>
      <div className="form-group columns">
        <div className="col-1">
          <label htmlFor="customer-id" className="panel-body-label">Customer</label>
        </div>
        <div className="col-6">
          <AutoCompleteInput
            id="customer-id"
            name="customerId"
            category="customer"
            placeholder="Enter customer id..."
            className="form-select"
            required
            value={ customer || '' }
            disabled={ (!forms || forms.length === 0) }
            onSelectedItem={ value => setCustomer(value) }
            onChange={ () => { setCustomer(null); setAccount(null); } }
          />
        </div>
      </div>
      <div className={ `form-group columns ${ !customer ? 'hide' : '' }` }>
        <div className="col-1">
          <label htmlFor="account-id" className="panel-body-label">Account</label>
        </div>
        <div className="col-6">
          <AutoCompleteInput
            id="account-id"
            name="accountId"
            category="account"
            placeholder="Enter account number..."
            className="form-select"
            context={ customer ? { type: 'customer', id: parseInt(customer.id, 10), value: '' } : null }
            required
            value={ account || '' }
            exclude={ sourceAccount ? [`${ sourceAccount.accountNumber } - ${ sourceAccount.name }`] : null }
            onSelectedItem={ value => setAccount(value) }
            onChange={ e => setAccount(null) }
          />
        </div>
      </div>
      <div className="form-group columns">
        <div className="col-1" />
        <div className="col-6">
          <button
            id="move-submit-btn"
            type="submit"
            className="btn btn-primary"
            disabled={ isMovingForm || (!formId || !customer || !account) }
          >
            { isMovingForm && <span className="icon loading mr" /> }
            Move Form
          </button>
        </div>
      </div>
    </form>
  </div>);
};

MoveForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  introText: PropTypes.string.isRequired,
  sourceAccountId: PropTypes.number,
  forms: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isMovingForm: PropTypes.bool.isRequired,
  moveFormComplete: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func
};

MoveForm.defaultProps = {
  sourceAccountId: null,
  onSuccess: null
};

const MoveReduxForm = reduxForm({
  form: 'move-form'
})(MoveForm);

const mapDispatchToProps = dispatch => ({ dispatch });

function mapStateToProps(state) {
  const {
    forms: {
      isMovingForm,
      moveFormComplete
    }
  } = state;

  return {
    isMovingForm,
    moveFormComplete
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MoveReduxForm);
